import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Pencil, X } from "lucide-react";
import MultiDateSlotGrid from "./MultiDateSlotGrid";
import { time12h } from "../lib/dates";

/**
 * Iter 34 — Admin Modify Booking modal
 *
 * • Shows each currently-booked slot as a chip with an X (individual cancel).
 * • Add new slots via the slot grid (existing behaviour).
 * • Discount (₹) input — mirrors the manual-booking modal.
 * • Live recompute: Subtotal, Discount, New Total, Paid, Balance / Refund.
 *   Refund honours the tiered policy (>24h = 100%, 24h–3h = 50%, <3h = 0%).
 */
export default function ModifyBookingModal({ booking, onClose, onSaved }) {
    const [centre, setCentre] = useState(null);
    const [facilityId, setFacilityId] = useState(booking.facility_id || "");
    const [date, setDate] = useState(booking.booking_date);
    const [slots, setSlots] = useState((booking.slots || []).slice().sort());
    const [discount, setDiscount] = useState(Number(booking.discount || 0));
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get(`/centres/${booking.centre_id}`).then(({ data }) => {
            setCentre(data);
            if (!facilityId && data.facilities?.length) setFacilityId(data.facilities[0].id);
        });
    }, [booking.centre_id]); // eslint-disable-line

    const facility = useMemo(() => (centre?.facilities || []).find(f => f.id === facilityId), [centre, facilityId]);
    const sortedFacilities = useMemo(() => (
        (centre?.facilities || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    ), [centre]);

    const halfPrice = facility ? Math.floor(facility.hourly_price / 2) : 0;
    const approxSubtotal = slots.length * halfPrice;
    const safeDiscount = Math.max(0, Math.min(Number(discount || 0), approxSubtotal));
    const newTotal = Math.max(0, approxSubtotal - safeDiscount);
    const paid = Number(booking.paid_amount || 0);
    const delta = newTotal - paid;

    // Tier refund preview (rough — backend recomputes authoritatively on save).
    const tierPct = useMemo(() => {
        if (delta >= 0) return null;
        const slotIso = `${booking.booking_date}T${booking.start_time || "00:00"}:00`;
        const hoursTo = (new Date(slotIso) - new Date()) / 3600000;
        if (hoursTo > 24) return 100;
        if (hoursTo > 3) return 50;
        return 0;
    }, [delta, booking.booking_date, booking.start_time]);
    const tierRefund = delta < 0 ? Math.floor((-delta) * (tierPct ?? 0) / 100) : 0;

    const removeSlot = (s) => setSlots(prev => prev.filter(x => x !== s));

    const submit = async () => {
        if (slots.length < 1) { toast.error("Pick at least 1 slot"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.put(`/admin/bookings/${booking.id}/modify`, {
                booking_date: date, slots, facility_id: facilityId, discount: safeDiscount,
            });
            const d = Number(data.delta || 0);
            if (d > 0) {
                toast.success(`Modified · Customer owes additional ₹${d} at venue`);
            } else if (d < 0) {
                const refundAmt = Number(data.modify_refund_amount || 0);
                const pct = Number(data.modify_refund_tier_pct ?? 0);
                if (refundAmt > 0) {
                    toast.success(`Modified · ₹${refundAmt} refund (${pct}%) pending — issue from booking row`);
                } else {
                    toast.success("Modified · No refund (within cancellation window)");
                }
            } else {
                toast.success("Booking modified");
            }
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
            <div className="w-full max-w-4xl bg-slate-900 border border-slate-800 rounded-lg p-6 my-6" data-testid="modify-booking-modal">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <Pencil className="w-5 h-5 text-sky-400" />
                        <h3 className="font-display text-2xl font-bold uppercase text-white">Modify Booking</h3>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="modify-close"><X className="w-5 h-5" /></button>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                    {booking.booking_code} · {booking.user_name} · originally {booking.booking_date} {time12h(booking.start_time)}–{time12h(booking.end_time)}
                </p>

                {centre && (
                    <>
                        <div className="mt-4">
                            <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Facility</label>
                            <select value={facilityId} onChange={(e) => setFacilityId(e.target.value)} data-testid="modify-facility-select"
                                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white">
                                {sortedFacilities.map(f => <option key={f.id} value={f.id}>{f.name} · ₹{f.hourly_price}/hr</option>)}
                            </select>
                        </div>

                        {/* Currently-selected slots with individual X buttons */}
                        <div className="mt-4">
                            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">
                                Current Slots ({slots.length})
                                <span className="ml-2 text-slate-600 normal-case tracking-normal">— click <X className="w-3 h-3 inline-block" /> to remove</span>
                            </div>
                            <div className="flex flex-wrap gap-2" data-testid="modify-slot-chips">
                                {slots.length === 0 && <div className="text-xs text-slate-500">No slots — add at least one below.</div>}
                                {slots.map(s => (
                                    <span key={s} className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-sky-500/10 border border-sky-500/40 text-sky-300 text-xs font-bold uppercase tracking-widest"
                                        data-testid={`slot-chip-${s.replace(":", "")}`}>
                                        {time12h(s)}
                                        <button onClick={() => removeSlot(s)} className="text-red-400 hover:text-red-300" title="Remove this slot"
                                            data-testid={`slot-remove-${s.replace(":", "")}`}>
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="mt-4">
                            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">Add / Replace Slots</div>
                            <MultiDateSlotGrid
                                centreId={centre.id}
                                facilityId={facilityId}
                                selectedDate={date}
                                selectedTimes={slots}
                                onSelectionChange={(d, t) => { setDate(d); setSlots(t.slice().sort()); }}
                            />
                        </div>

                        {/* Discount + live preview */}
                        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Discount (₹)</label>
                                <input type="number" min="0" value={discount}
                                    onChange={(e) => setDiscount(Math.max(0, Number(e.target.value || 0)))}
                                    data-testid="modify-discount"
                                    className="block mt-1 w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm" />
                                <p className="text-[10px] text-slate-500 mt-1">Subtracted from subtotal before total is computed.</p>
                            </div>
                            <div className="bg-slate-950 border border-slate-800 rounded p-3 text-sm" data-testid="modify-preview">
                                <Row label="Slots" value={`${slots.length} × ${halfPrice ? `₹${halfPrice}` : "—"}`} />
                                <Row label="Subtotal" value={`₹${approxSubtotal.toLocaleString("en-IN")}`} />
                                <Row label="Discount" value={`− ₹${safeDiscount.toLocaleString("en-IN")}`} muted={safeDiscount === 0} />
                                <div className="border-t border-slate-800 my-1.5" />
                                <Row label="New Total" value={`₹${newTotal.toLocaleString("en-IN")}`} bold testid="modify-new-total" />
                                <Row label="Already Paid" value={`₹${paid.toLocaleString("en-IN")}`} />
                                {delta > 0 && (
                                    <Row label="Balance to Collect" value={`₹${delta.toLocaleString("en-IN")}`} colorCls="text-amber-400" bold testid="modify-balance" />
                                )}
                                {delta < 0 && (
                                    <>
                                        <Row label={`Refund (${tierPct ?? 0}% tier)`} value={`₹${tierRefund.toLocaleString("en-IN")}`} colorCls="text-[#00FF66]" bold testid="modify-refund-amount" />
                                        {tierPct === 50 && <p className="text-[10px] text-amber-400 mt-1">⚠ 50% tier — admin issues refund from the booking row.</p>}
                                        {tierPct === 0 && <p className="text-[10px] text-red-400 mt-1">⚠ Within 3 h of start — no refund per policy.</p>}
                                    </>
                                )}
                                {delta === 0 && <Row label="Balance" value="₹0" muted />}
                            </div>
                        </div>
                    </>
                )}

                <div className="flex gap-3 pt-5">
                    <button onClick={onClose} className="flex-1 h-11 border border-slate-700 rounded text-slate-300">Cancel</button>
                    <button onClick={submit} disabled={submitting || slots.length === 0} data-testid="modify-submit"
                        className="flex-1 h-11 bg-sky-400 text-slate-950 font-bold uppercase tracking-widest rounded disabled:opacity-40">
                        {submitting ? "Saving..." : "Save Modification"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, bold, muted, colorCls, testid }) {
    return (
        <div className="flex items-center justify-between py-0.5" {...(testid ? { "data-testid": testid } : {})}>
            <span className={`text-xs uppercase tracking-widest ${muted ? "text-slate-600" : "text-slate-400"}`}>{label}</span>
            <span className={`${bold ? "font-bold text-base" : "text-sm"} ${colorCls || (muted ? "text-slate-500" : "text-white")}`}>{value}</span>
        </div>
    );
}
